import {
  Component,
  inject,
  input,
  InputSignal,
  OnInit,
  output,
  OutputEmitterRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormInputComponent } from './form-input.component';
import { FormBuilder, FormControl, NgControl } from '@angular/forms';
import { SelectionOptionValueToNamePipe } from '../../../pipes/selection-option-value-to-name/selection-option-value-to-name.pipe';

export interface IObjectOption {
  [key: string]: unknown;
}

type SimpleOption = unknown;

export type SelectOption = IObjectOption | SimpleOption;

@Component({
  selector: 'aw-form-input-select',
  imports: [CommonModule],
  template: '',
})
export class FormInputSelectComponent
  extends FormInputComponent
  implements OnInit
{
  public options: InputSignal<SelectOption[]> =
    input.required<SelectOption[]>();
  public valueAttribute: InputSignal<string | undefined> = input<string>();
  public name: InputSignal<string | undefined> = input<string>();
  public isLoading: InputSignal<boolean> = input<boolean>(false);

  public readonly onSearch: OutputEmitterRef<string> = output();

  private readonly formBuilder: FormBuilder = inject(FormBuilder);

  protected readonly toSelectOptionName: SelectionOptionValueToNamePipe =
    new SelectionOptionValueToNamePipe();
  public readonly searchControl: FormControl<string> =
    this.formBuilder.nonNullable.control<string>('');

  constructor(public override ngControl: NgControl) {
    super(ngControl);
    this.subscription.add(
      this.searchControl.valueChanges.subscribe((value: string) =>
        this.onSearch.emit(value)
      )
    );
  }

  ngOnInit() {
    this.subscription.add(
      this.ngControl.control!.valueChanges.subscribe((value: IObjectOption) => {
        const matchingValue: { [index: string]: any } | undefined =
          this.findMatchingOption(value, true);
        this.searchControl.setValue(
          this.name() && !this.valueAttribute()
            ? this.ngControl.control!.value?.[this.name() as unknown as string]
            : this.name()
              ? value?.[this.name() as unknown as string] ||
                matchingValue?.[this.name()! as unknown as string]
              : this.ngControl.control!.value
        );
      })
    );
  }

  public selectOption(option: SelectOption): void {
    if (!this.ngControl.control!.disabled) {
      const findValue = (option: SelectOption, value?: string) => {
        return option &&
          value &&
          Object.prototype.hasOwnProperty.call(option as IObjectOption, value)
          ? (option as IObjectOption)[value]
          : option;
      };
      this.ngControl.control!.setValue(
        findValue(option, this.valueAttribute())
      );
      this.ngControl.control!.markAsDirty();
      this.ngControl.control!.markAsTouched();
    }
  }

  protected findIndexFromValue(value: unknown): number | undefined {
    const matchingOption: SelectOption =
      this.toSelectOptionName.findOptionByValue(
        value,
        this.options(),
        this.name()!,
        this.valueAttribute()
      );
    return this.options().findIndex(option => option == matchingOption);
  }

  public tryToMatchSearchTerm(): void {
    this.ngControl.control!.markAsTouched();
    const directOption: { [index: string]: any } | undefined =
      this.findMatchingOption(this.searchControl.value);
    if (
      directOption &&
      directOption != this.ngControl.control!.value &&
      (!this.valueAttribute() ||
        directOption?.[this.valueAttribute() as unknown as string] !=
          this.ngControl.control!.value)
    ) {
      this.selectOption(directOption);
    } else if (this.searchControl.value == '') {
      this.ngControl.control!.setValue(undefined);
      this.onSearch.emit('');
    } else if (!directOption) {
      this.ngControl.control!.setValue(undefined, { emitEvent: false });
    }
  }

  public findMatchingOption(
    value: SelectOption | string,
    isSearch: boolean = false
  ): { [index: string]: any } | undefined {
    return this.options().find((option: SelectOption) => {
      return (
        option + '' == value ||
        (option as IObjectOption)[this.name() as string] == value ||
        (((this.valueAttribute() && !this.name()) || isSearch) &&
          (option as IObjectOption)[this.valueAttribute() as string] == value)
      );
    }) as { [index: string]: any };
  }
}
